import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { Send, Save } from "lucide-react";

export default function AISettings() {
  const { activeTenant } = useTenant();
  const [settings, setSettings] = useState(null);
  const [saved, setSaved] = useState(false);

  const [sessionId] = useState(() => `tester-${Math.random().toString(36).slice(2, 8)}`);
  const [chat, setChat] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    if (!activeTenant) return;
    const { data } = await api.get("/ai-settings");
    setSettings(data);
  };
  useEffect(() => {
    load();
    setChat([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTenant?.id]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [chat]);

  const save = async () => {
    await api.put("/ai-settings", {
      system_prompt: settings.system_prompt,
      model: settings.model,
      provider: settings.provider,
      tone: settings.tone,
      welcome_message: settings.welcome_message,
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const sendMsg = async (e) => {
    e.preventDefault();
    if (!input.trim() || busy) return;
    const userMsg = { role: "user", content: input };
    setChat((c) => [...c, userMsg]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/ai-chat", { session_id: sessionId, message: userMsg.content });
      setChat((c) => [...c, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setChat((c) => [...c, { role: "assistant", content: `[error] ${err.response?.data?.detail || err.message}` }]);
    } finally {
      setBusy(false);
    }
  };

  if (!activeTenant || !settings) return <div className="p-10 text-[var(--n-text-muted)]">Loading…</div>;

  return (
    <div className="p-8 grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="ai-settings-page">
      {/* Left: settings */}
      <div className="n-card p-5">
        <div className="n-label">AI Agent · {activeTenant.name}</div>
        <h1 className="font-cab font-black text-3xl tracking-tight mt-1 mb-5">Agent configuration</h1>

        <label className="n-label block mb-1">System prompt</label>
        <textarea
          rows={10}
          className="n-input font-mono-plex text-xs"
          value={settings.system_prompt}
          onChange={(e) => setSettings({ ...settings, system_prompt: e.target.value })}
          data-testid="ai-system-prompt"
        />

        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <label className="n-label block mb-1">Provider</label>
            <select
              className="n-input"
              value={settings.provider}
              onChange={(e) => setSettings({ ...settings, provider: e.target.value })}
              data-testid="ai-provider"
            >
              <option value="anthropic">anthropic</option>
              <option value="openai">openai</option>
              <option value="gemini">gemini</option>
            </select>
          </div>
          <div>
            <label className="n-label block mb-1">Model</label>
            <input
              className="n-input font-mono-plex text-xs"
              value={settings.model}
              onChange={(e) => setSettings({ ...settings, model: e.target.value })}
              data-testid="ai-model"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <label className="n-label block mb-1">Tone</label>
            <select
              className="n-input"
              value={settings.tone}
              onChange={(e) => setSettings({ ...settings, tone: e.target.value })}
              data-testid="ai-tone"
            >
              {["friendly", "professional", "concise", "playful", "empathetic"].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="n-label block mb-1">Welcome msg</label>
            <input
              className="n-input"
              value={settings.welcome_message}
              onChange={(e) => setSettings({ ...settings, welcome_message: e.target.value })}
              data-testid="ai-welcome"
            />
          </div>
        </div>

        <button onClick={save} className="n-btn-blue n-btn mt-5 flex items-center gap-2" data-testid="ai-save">
          <Save size={14} /> {saved ? "SAVED ✓" : "SAVE SETTINGS"}
        </button>
      </div>

      {/* Right: live tester */}
      <div className="n-card p-5 flex flex-col" style={{ minHeight: "70vh" }}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="n-label">Live tester</div>
            <h2 className="font-cab font-bold text-xl mt-1">Try the agent</h2>
          </div>
          <span className="n-chip">{settings.model}</span>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-auto border border-[var(--n-border)] bg-[#0a0a0a] p-3 mb-3 font-mono-plex text-xs space-y-2" data-testid="ai-chat-tester-output">
          {chat.length === 0 && (
            <div className="text-[var(--n-text-muted)]">
              <span className="text-[var(--n-success)]">[agent]</span> {settings.welcome_message}
            </div>
          )}
          {chat.map((m, i) => (
            <div key={i} className={m.role === "user" ? "text-white" : "text-[var(--n-text-sec)]"}>
              <span className={m.role === "user" ? "text-[var(--n-blue)]" : "text-[var(--n-success)]"}>
                [{m.role}]
              </span>{" "}
              {m.content}
            </div>
          ))}
          {busy && (
            <div className="text-[var(--n-text-muted)]">
              <span className="text-[var(--n-success)]">[agent]</span> <span className="pulse-dot">thinking…</span>
            </div>
          )}
        </div>

        <form onSubmit={sendMsg} className="flex gap-2">
          <input
            className="n-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask the agent something…"
            data-testid="ai-chat-tester-input"
          />
          <button className="n-btn-blue n-btn flex items-center gap-2" type="submit" disabled={busy} data-testid="ai-chat-tester-send">
            <Send size={14} /> SEND
          </button>
        </form>
      </div>
    </div>
  );
}
